import { computed, ref } from 'vue';
import { FEATURE_FLAG, getFeatureFlag } from '@phoenix/helpers/feature-flag-helper';

const STORAGE_KEY = 'phoenixConsents';

const isConsentRequired = () => getFeatureFlag(FEATURE_FLAG.CONSENT_FOR_EXTERNAL_CONTENT);

export const CONSENT_NAMES = {
  gmaps: 'gmaps',
  youtube: 'youtube',
};

let initialized = false;
const acceptedConsents = ref([]);

const readStoredConsents = () => {
  try {
    const storedValue = localStorage.getItem(STORAGE_KEY);
    const parsedValue = storedValue ? JSON.parse(storedValue) : [];

    return Array.isArray(parsedValue) ? parsedValue : [];
  } catch {
    return [];
  }
};

const writeStoredConsents = () => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(acceptedConsents.value));
  } catch {
    // Ignore storage errors: the reactive state remains valid for the current page.
  }
};

const initializeConsents = () => {
  if (initialized) {
    return;
  }

  acceptedConsents.value = readStoredConsents();

  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) {
      acceptedConsents.value = readStoredConsents();
    }
  });

  initialized = true;
};

export const useConsent = () => {
  initializeConsents();

  const isConsentAccepted = (name) => computed(() => !isConsentRequired() || acceptedConsents.value.includes(name));

  const setConsent = (name, accepted) => {
    const consents = new Set(acceptedConsents.value);

    if (accepted) {
      consents.add(name);
    } else {
      consents.delete(name);
    }

    acceptedConsents.value = Array.from(consents);
    writeStoredConsents();
  };

  const acceptConsent = (name) => setConsent(name, true);

  const revokeConsent = (name) => setConsent(name, false);

  return {
    isConsentAccepted,
    acceptConsent,
    revokeConsent,
  };
};
